import React from "react";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import { LinkPresentationProps } from "@insite/mobius/Link";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import LazyImage from "@insite/mobius/LazyImage";
import { css } from "styled-components";
import BasicList from "@WhiteCap/Components/BasicList";
import WhitecapColors from "@WhiteCap/Utils/WhiteCapColor";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Divider from "@WhiteCap/Components/Basic/Divider";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import GenrateUrl from "@WhiteCap/Components/Basic/GenrateUrl";
import getColor from "@insite/mobius/utilities/getColor";

const enum fields {
    title = "title",
    desc = "desc",
    gap = "gap",
    categories = "categories",
}

export interface SafetySystemModel {
    fields: {
        image: string;
        caption: string;
        linkText: string;
        link: LinkFieldValue;
        openInNewWindow: boolean;
    };
}

export interface SafetySystemCategoryModel {
    fields: {
        name: string;
        gap: number;
        safetySystemItem: SafetySystemModel[];
    };
}
export interface FieldsProps {
    [fields.title]: string;
    [fields.desc]: string;
    [fields.gap]: number;
    [fields.categories]: SafetySystemCategoryModel[];
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.desc]: string;
        [fields.gap]: number;
        [fields.categories]: SafetySystemCategoryModel[];
    };
}

export interface SafetySystemStyles {
    mainGridContainer?: GridContainerProps;
    mainGridItem?: GridItemProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyProps;
    productContainer?: InjectableCss;
    imageContainer?: InjectableCss;
    caption?: TypographyProps;
    link?: LinkPresentationProps;
    gapBetweenCategories?: InjectableCss;
}

const safetySystemStyle: SafetySystemStyles = {
    mainGridContainer: {
        css: css`
            margin-top: 50px;
        `,
    },
    headingGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    heading: {
        css: css`
            font-size: 18px;
            font-weight: 500;
            text-transform: uppercase;
            letter-spacing: 1px;
            line-height: 1.5;
        `,
    },
    productContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            gap: 10px;
        `,
    },
    imageContainer: {
        css: css`
            min-height: 175px;
        `,
    },
    caption: {
        css: css`
            font-weight: 500;
            text-transform: uppercase;
        `,
    },
    link: {
        color: WhitecapColors.orangeLink,
        css: css`
            font-size: 14px;
            &:hover {
                text-decoration: underline;
                ${TypographyStyle} {
                    color: ${getColor("text.main")};
                }
            }
        `,
    },
    mainGridItem: {
        css: css`
            justify-content: center;
        `,
    },
};

const styles = safetySystemStyle;

const SafetySystem = ({ fields }: OwnProps) => {
    styles.gapBetweenCategories = {
        css: css`
            margin-bottom: ${fields.gap}px;
        `,
    };

    return (
        <StyledWrapper>
            <BasicList fields={fields} />
            <GridContainer {...styles.mainGridContainer}>
                {fields.categories.map((item, index) => (
                    <GridItem width={12} key={`${item.fields.name}_${index}`} {...styles.gapBetweenCategories}>
                        <GridContainer id={`${item.fields.name.replace(" ", "-")}`} {...styles.gapBetweenCategories}>
                            <GridItem width={12} {...styles.headingGridItem}>
                                <Typography variant="h3" {...styles.heading}>
                                    {item.fields.name}
                                </Typography>
                                <Divider borderTop={`1px solid ${WhitecapColors.borderGray}`} />
                            </GridItem>

                            {item.fields.safetySystemItem &&
                                item.fields.safetySystemItem.map(product => (
                                    <GridItem
                                        width={[12, 12, 3, 3, 3]}
                                        key={product.fields.caption}
                                        {...styles.mainGridItem}
                                    >
                                        <StyledWrapper {...styles.productContainer}>
                                            <StyledWrapper {...styles.imageContainer}>
                                                <LazyImage src={product.fields.image} />
                                            </StyledWrapper>
                                            <Typography {...styles.caption}>{product.fields.caption}</Typography>

                                            <GenrateUrl
                                                link={product.fields.link}
                                                style={styles.link}
                                                openInNewTab={product.fields.openInNewWindow}
                                            >
                                                {product.fields.linkText}
                                            </GenrateUrl>
                                        </StyledWrapper>
                                    </GridItem>
                                ))}
                        </GridContainer>
                    </GridItem>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const SafetySystemModule: WidgetModule = {
    component: SafetySystem,
    definition: {
        displayName: "Categories & Multiple Product List",
        fieldDefinitions: [
            {
                name: "title",
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "desc",
                displayName: "Description",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: "gap",
                displayName: "Gap between categories",
                editorTemplate: "IntegerField",
                defaultValue: 48,
                fieldType: "General",
            },
            {
                name: fields.categories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "Translatable",
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Category Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },

                    {
                        name: "safetySystemItem",
                        editorTemplate: "ListField",
                        getDisplay: (item: HasFields) => {
                            return item.fields.caption;
                        },
                        defaultValue: [],
                        fieldDefinitions: [
                            {
                                name: "image",
                                displayName: "Icon Image",
                                editorTemplate: "ImagePickerField",
                                defaultValue: "",
                            },

                            {
                                name: "caption",
                                displayName: "Caption",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },

                            {
                                name: "linkText",
                                displayName: "Link Text",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                            {
                                name: "link",
                                displayName: "Link",
                                editorTemplate: "LinkField",
                                defaultValue: {
                                    value: "",
                                    type: "Url",
                                },
                            },
                            {
                                name: "openInNewWindow",
                                editorTemplate: "CheckboxField",
                                defaultValue: true,
                            },
                        ],
                    },
                ],
            },
        ],
        group: "Common",
    },
};

export default SafetySystemModule;
